/* Violet Eyes cart drawer, computers only (>= 990px). Draws the list from /cart.js. */
(() => {
  if (customElements.get('ve-cart-drawer')) return;
  const wide = window.matchMedia('(min-width: 990px)');

  class VeCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.ve-cd__panel');
      this.itemsEl = this.querySelector('[data-ve-cd-items]');
      this.foot = this.querySelector('[data-ve-cd-foot]');
      this.extras = this.querySelector('[data-ve-cd-extras]');
      this.emptyCta = this.querySelector('[data-ve-cd-empty]');
      this.titleEl = this.querySelector('[data-ve-cd-title]');
      this.subtotalEl = this.querySelector('[data-ve-cd-subtotal]');
      this.agree = this.querySelector('[data-ve-cd-agree]');
      this.error = this.querySelector('[data-ve-cd-error]');
      this.noteToggle = this.querySelector('[data-ve-cd-note-toggle]');
      this.note = this.querySelector('[data-ve-cd-note]');
      this.i18n = JSON.parse(this.querySelector('[data-ve-cd-i18n]').textContent);
      this.attrName = this.dataset.attributeName || 'Return policy accepted';
      this.tz = this.dataset.timezone || 'Asia/Jerusalem';
      this.pending = Promise.resolve();
      this.hidden = false;
      // load the cart once the page is idle, so the first click on Cart has the list ready
      const warm = () => this.refresh().catch(() => {});
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 });
      else setTimeout(warm, 800);

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-ve-cd-close]')) return this.close();
        const rm = e.target.closest('[data-ve-cd-remove]');
        if (rm) return this.remove(rm.dataset.veCdRemove);
        if (e.target.closest('[data-ve-cd-note-toggle]')) {
          const open = this.note.hidden;
          this.note.hidden = !open;
          this.noteToggle.setAttribute('aria-expanded', String(open));
          if (open) this.note.focus();
          return;
        }
        const out = e.target.closest('[data-ve-cd-checkout]');
        if (out) return this.checkout(e, out);
      });
      // Esc closes the drawer wherever the focus is (listener lives on the page only while the drawer is open)
      this.onKey = (e) => {
        if (e.key === 'Escape') this.close();
      };
      if (this.agree) {
        this.agree.addEventListener('change', () => {
          this.error.hidden = true;
          this.saveAttr(this.agree.checked ? this.stamp() : '');
        });
      }
      if (this.note) {
        this.note.addEventListener('change', () => {
          this.pending = this.pending.catch(() => {}).then(() => this.post('/cart/update.js', { note: this.note.value }));
        });
      }
    }

    root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    post(path, body) {
      return fetch(this.root() + path.replace(/^\//, ''), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json());
    }

    stamp() {
      const now = new Date();
      try {
        return (
          new Intl.DateTimeFormat('en-GB', { timeZone: this.tz, dateStyle: 'medium', timeStyle: 'medium' }).format(now) +
          ' (' + this.tz + ')'
        );
      } catch (e) {
        return now.toISOString();
      }
    }

    saveAttr(value) {
      const attributes = {};
      attributes[this.attrName] = value;
      this.pending = this.pending.catch(() => {}).then(() => this.post('/cart/update.js', { attributes }));
      return this.pending;
    }

    money(cents, cart) {
      const value = cents / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: cart.currency || 'ILS',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(value);
      } catch (e) {
        return value.toFixed(2);
      }
    }

    esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    }

    img(src) {
      if (!src) return '';
      try {
        const u = new URL(src, location.origin);
        u.searchParams.set('width', '300');
        return u.toString();
      } catch (e) {
        return src;
      }
    }

    async refresh() {
      const cart = await fetch(this.root() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.render(cart);
      this.loaded = true;
      return cart;
    }

    render(cart) {
      const n = cart.item_count;
      this.titleEl.textContent = `${this.i18n.title} [${n}]`;
      this.classList.toggle('ve-cd--two', cart.items.length === 2);
      this.classList.toggle('ve-cd--many', cart.items.length >= 3);
      if (!cart.items.length) {
        this.itemsEl.innerHTML = `<div class="ve-cd__empty"><p>${this.esc(this.i18n.empty)}</p><a href="${this.esc(this.dataset.accountUrl || '/account/login')}">${this.esc(this.i18n.missing)}</a></div>`;
        // empty: only the Shipping and Subtotal rows stay (no note, tick or buttons)
        this.foot.hidden = false;
        if (this.extras) this.extras.hidden = true;
        if (this.emptyCta) this.emptyCta.hidden = false;
      } else {
        this.foot.hidden = false;
        if (this.extras) this.extras.hidden = false;
        if (this.emptyCta) this.emptyCta.hidden = true;
        this.itemsEl.innerHTML = cart.items
          .map((it) => {
            const opts = (it.options_with_values || [])
              .filter((o) => o.value && o.value !== 'Default Title')
              .map((o) => `${this.esc(o.name)} ${this.esc(o.value)}`)
              .join(' · ');
            const photo = it.image
              ? `<img src="${this.esc(this.img(it.image))}" alt="${this.esc(it.featured_image && it.featured_image.alt ? it.featured_image.alt : '')}" loading="lazy">`
              : '';
            return `<article class="ve-cd__item">
              <a class="ve-cd__photo" href="${this.esc(it.url)}" tabindex="-1" aria-hidden="true">${photo}</a>
              <div class="ve-cd__info">
                <div class="ve-cd__top">
                  <a class="ve-cd__name" href="${this.esc(it.url)}">${this.esc(it.product_title)}</a>
                  <div class="ve-cd__price">${this.esc(this.money(it.final_line_price, cart))}</div>
                </div>
                ${opts ? `<div class="ve-cd__meta">${opts}</div>` : ''}
                <button type="button" class="ve-cd__remove" data-ve-cd-remove="${this.esc(it.key)}">${this.esc(this.i18n.remove)}</button>
              </div>
            </article>`;
          })
          .join('');
      }
      this.subtotalEl.textContent = this.money(cart.items_subtotal_price, cart);
      if (this.agree) this.agree.checked = !!(cart.attributes && cart.attributes[this.attrName]);
      if (this.note && document.activeElement !== this.note) {
        this.note.value = cart.note || '';
        if (cart.note) {
          this.note.hidden = false;
          this.noteToggle.setAttribute('aria-expanded', 'true');
        }
      }
      // keep the Cart [n] in the header bar in step
      document.querySelectorAll('#cart-icon-bubble .ve-count').forEach((el) => (el.textContent = `[${n}]`));
      document.querySelectorAll('#cart-icon-bubble .ve-cart__n').forEach((el) => (el.textContent = n));
    }

    async remove(key) {
      const cart = await this.post('/cart/change.js', { id: key, quantity: 0 });
      this.render(cart);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 've-cart-drawer', cartData: cart });
      }
    }

    checkout(event, link) {
      event.preventDefault();
      if (this.agree && !this.agree.checked) {
        this.error.hidden = false;
        this.agree.focus();
        return;
      }
      if (this.leaving) return;
      this.leaving = true;
      const go = () => (window.location.href = link.href);
      if (this.agree && !(this.lastStamped)) {
        this.lastStamped = true;
        this.saveAttr(this.stamp()).then(go, go);
      } else {
        this.pending.then(go, go);
      }
    }

    // wait = true (after Add to cart): load the fresh cart first, so the new piece is already in the list when it slides in.
    // From the Cart link it slides in at once and the list refreshes while it moves (the cart was loaded when the page opened).
    async open(wait) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.opener = document.activeElement;
      this.lastStamped = false;
      this.leaving = false;
      this.error.hidden = true;
      if (wait || !this.loaded) {
        try { await this.refresh(); } catch (e) {}
      } else {
        this.refresh().catch(() => {});
      }
      this.classList.add('is-open');
      document.addEventListener('keydown', this.onKey);
      document.documentElement.style.overflow = 'hidden';
      setTimeout(() => this.panel.focus(), 50);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKey);
      document.documentElement.style.overflow = '';
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }
  customElements.define('ve-cart-drawer', VeCartDrawer);

  const drawer = () => document.querySelector('ve-cart-drawer');
  const onCartPage = () => /\/cart\/?$/.test(location.pathname);

  // narrower than 990px while open (window resized): close, or the page would stay locked from scrolling
  const onWidth = (e) => {
    if (e.matches) return;
    const d = drawer();
    if (d && d.isOpen) d.close();
  };
  if (wide.addEventListener) wide.addEventListener('change', onWidth);
  else if (wide.addListener) wide.addListener(onWidth);

  // Cart link in the header bar
  document.addEventListener(
    'click',
    (e) => {
      if (!wide.matches || onCartPage() || e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target.closest && e.target.closest('#cart-icon-bubble');
      const d = drawer();
      if (!link || !d) return;
      e.preventDefault();
      e.stopPropagation();
      d.open();
    },
    true
  );

  // After "Add to cart"
  const hook = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!wide.matches || onCartPage()) return;
        if (event && (event.source === 'product-form' || event.source === 'quick-add')) {
          const d = drawer();
          if (d) setTimeout(() => d.open(true), 120);
        }
      });
    }
    // the old pop-up must not open, or it would take the focus
    customElements.whenDefined('cart-notification').then(() => {
      const proto = customElements.get('cart-notification').prototype;
      const original = proto.open;
      if (proto.__veDrawerPatched) return;
      proto.__veDrawerPatched = true;
      proto.open = function (...args) {
        if (wide.matches && !onCartPage()) return;
        return original.apply(this, args);
      };
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hook);
  else hook();
})();
